import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@workspace/ui/components/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@workspace/ui/components/table";
import { KeyRoundIcon, PlusIcon, SearchIcon, ShieldCheckIcon, Trash2Icon, UserRoundCogIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { orpc } from "@/api/orpc";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Main } from "@/layout/main";
import { getError } from "@/utils/error";
import { fDateTime } from "@/utils/format-time";

export const Route = createFileRoute("/_authenticated/staff")({
  beforeLoad: ({ context }) => {
    if (context.dashboardRole !== "admin") throw redirect({ to: "/403", replace: true });
  },
  component: StaffUsersPage,
});

type StaffRole = "admin" | "editor";

const emptyStaffForm = {
  name: "",
  email: "",
  phoneNumber: "",
  role: "editor" as StaffRole,
  password: "",
  confirmPassword: "",
};

function StaffUsersPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"name" | "createdAt" | "role">("createdAt");
  const [direction, setDirection] = useState<"asc" | "desc">("desc");
  const [createOpen, setCreateOpen] = useState(false);
  const [passwordUser, setPasswordUser] = useState<{ id: string; name: string }>();
  const [deleteUser, setDeleteUser] = useState<{ id: string; name: string }>();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [form, setForm] = useState(emptyStaffForm);

  const listInput = { page, perPage: 50, search: search || undefined, sort, direction } as const;
  const staffQuery = useQuery(orpc.admin.staff.list.queryOptions({ input: listInput }));

  const refresh = async () => {
    await Promise.all([
      staffQuery.refetch(),
      queryClient.invalidateQueries({ queryKey: orpc.admin.dashboard.summary.queryKey() }),
    ]);
  };

  const createStaff = useMutation(
    orpc.admin.staff.create.mutationOptions({
      onSuccess: async () => {
        await refresh();
        setForm(emptyStaffForm);
        setCreateOpen(false);
        toast.success("Dashboard user created");
      },
      onError: (error) => toast.error(getError(error, "Unable to create dashboard user")),
    }),
  );
  const setRole = useMutation(
    orpc.admin.staff.setRole.mutationOptions({
      onSuccess: async () => {
        await refresh();
        toast.success("Role updated");
      },
      onError: (error) => toast.error(getError(error, "Unable to update role")),
    }),
  );
  const setStatus = useMutation(
    orpc.admin.staff.setStatus.mutationOptions({
      onSuccess: async () => {
        await refresh();
        toast.success("Account status updated");
      },
      onError: (error) => toast.error(getError(error, "Unable to update account status")),
    }),
  );
  const setPassword = useMutation(
    orpc.admin.staff.setPassword.mutationOptions({
      onSuccess: () => {
        setPasswordUser(undefined);
        setNewPassword("");
        setConfirmPassword("");
        toast.success("Password reset successfully");
      },
      onError: (error) => toast.error(getError(error, "Unable to reset password")),
    }),
  );
  const deleteStaff = useMutation(
    orpc.admin.staff.delete.mutationOptions({
      onSuccess: async () => {
        await refresh();
        setDeleteUser(undefined);
        toast.success("Editor account deleted");
      },
      onError: (error) => toast.error(getError(error, "Unable to delete editor account")),
    }),
  );

  const submitCreate = () => {
    if (!form.name.trim() || !form.email.trim()) {
      return toast.error("Name and email are required");
    }
    if (form.password.length < 8) return toast.error("Password must contain at least 8 characters");
    if (form.password !== form.confirmPassword) return toast.error("Passwords do not match");
    createStaff.mutate({
      name: form.name,
      email: form.email,
      phoneNumber: form.phoneNumber.trim() || undefined,
      role: form.role,
      password: form.password,
    });
  };

  const submitPassword = () => {
    if (!passwordUser) return;
    if (newPassword.length < 8) return toast.error("Password must contain at least 8 characters");
    if (newPassword !== confirmPassword) return toast.error("Passwords do not match");
    setPassword.mutate({ userId: passwordUser.id, password: newPassword });
  };

  return (
    <Main className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">Dashboard users</h1>
          <p className="mt-1 text-muted-foreground text-sm">
            Add administrators and editors, control access, and reset passwords securely.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <PlusIcon /> Add dashboard user
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-64 flex-1 sm:max-w-sm">
          <SearchIcon className="absolute start-3 top-2.5 size-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Search name, email or phone…"
            className="ps-9"
          />
        </div>
        <Select
          value={sort}
          onValueChange={(value) => {
            setSort(value as typeof sort);
            setPage(1);
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="createdAt">Registration date</SelectItem>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="role">Role</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={direction}
          onValueChange={(value) => {
            setDirection(value as typeof direction);
            setPage(1);
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="desc">Descending</SelectItem>
            <SelectItem value="asc">Ascending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-end">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {staffQuery.data?.items.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.phoneNumber || "—"}</TableCell>
                <TableCell>
                  <Select
                    value={(user.role ?? "editor") as StaffRole}
                    onValueChange={(role) => setRole.mutate({ userId: user.id, role: role as StaffRole })}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="editor">Editor</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Badge variant={user.banned ? "destructive" : "secondary"}>
                    {user.banned ? "Disabled" : "Active"}
                  </Badge>
                </TableCell>
                <TableCell className="whitespace-nowrap">{fDateTime(user.createdAt)}</TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPasswordUser({ id: user.id, name: user.name })}
                    >
                      <KeyRoundIcon /> Password
                    </Button>
                    <Button
                      variant={user.banned ? "default" : "destructive"}
                      size="sm"
                      onClick={() => setStatus.mutate({ userId: user.id, banned: !user.banned })}
                    >
                      <UserRoundCogIcon /> {user.banned ? "Enable" : "Disable"}
                    </Button>
                    {user.role === "editor" && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setDeleteUser({ id: user.id, name: user.name })}
                      >
                        <Trash2Icon /> Delete
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!staffQuery.isLoading && !staffQuery.data?.items.length && (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  No dashboard users found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 text-muted-foreground text-sm">
        <p>{staffQuery.data?.totalItems ?? 0} dashboard users · 50 rows per page</p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!staffQuery.data?.hasPreviousPage}
            onClick={() => setPage((value) => value - 1)}
          >
            Previous
          </Button>
          <span>
            {staffQuery.data?.currentPage ?? page} / {Math.max(staffQuery.data?.totalPageNumbers ?? 1, 1)}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!staffQuery.data?.hasNextPage}
            onClick={() => setPage((value) => value + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add dashboard user</DialogTitle>
            <DialogDescription>The password is stored securely and never included in activity logs.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name">
              <Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
            </Field>
            <Field label="Phone (optional)">
              <Input
                value={form.phoneNumber}
                onChange={(event) => setForm({ ...form, phoneNumber: event.target.value })}
              />
            </Field>
            <Field label="Email" className="sm:col-span-2">
              <Input
                type="email"
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
              />
            </Field>
            <Field label="Role" className="sm:col-span-2">
              <Select value={form.role} onValueChange={(role) => setForm({ ...form, role: role as StaffRole })}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Password">
              <Input
                type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={(event) => setForm({ ...form, password: event.target.value })}
              />
            </Field>
            <Field label="Confirm password">
              <Input
                type="password"
                autoComplete="new-password"
                value={form.confirmPassword}
                onChange={(event) => setForm({ ...form, confirmPassword: event.target.value })}
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button disabled={createStaff.isPending} onClick={submitCreate}>
              <ShieldCheckIcon /> Create user
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!passwordUser} onOpenChange={(open) => !open && setPasswordUser(undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              Set a new permanent password for {passwordUser?.name}. Minimum 8 characters.
            </DialogDescription>
          </DialogHeader>
          <Field label="New password">
            <Input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </Field>
          <Field label="Confirm password">
            <Input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordUser(undefined)}>
              Cancel
            </Button>
            <Button disabled={setPassword.isPending} onClick={submitPassword}>
              Reset password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        title="Delete editor account?"
        desc={`This permanently deletes ${deleteUser?.name ?? "this editor"} and all of their active sessions.`}
        onOpenChange={(open) => !open && setDeleteUser(undefined)}
        destructive
        open={!!deleteUser}
        handleConfirm={() => deleteUser && deleteStaff.mutate({ userId: deleteUser.id })}
        isLoading={deleteStaff.isPending}
        confirmText="Delete editor"
      />
    </Main>
  );
}

function Field({ label, className, children }: { label: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <Label className="mb-2">{label}</Label>
      {children}
    </div>
  );
}
